import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  Spacer,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { LearnMore } from "src/components/Links/LearnMore";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useOrganization } from "src/hooks/useOrganization";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import {
  EnrollmentPeriodFormSchema,
  EnrollmentPeriodFormType,
} from "../schemas";

interface Props {
  enrollmentPeriod?: GQL.GetEnrollment_enrollment_period_by_pk;
  onSubmit: (values: EnrollmentPeriodFormType) => void;
  submitting: boolean;
}

export const GeneralForm: React.FC<Props> = ({
  onSubmit,
  submitting,
  enrollmentPeriod,
}) => {
  const organization = useOrganization();
  const isNewSeason = !enrollmentPeriod;
  const flags = useFlags([
    "service-offerings",
    "service-offerings-multi-seat-type",
  ]);
  const isServiceOfferingsEnabled = flags["service-offerings"].enabled;
  const isMultiSeatTypeEnabled =
    flags["service-offerings-multi-seat-type"].enabled;

  const initialValues: EnrollmentPeriodFormType = useMemo(
    () =>
      isNewSeason
        ? {
            name: "",
            active: false,
            key: "",
            use_service_offerings: isServiceOfferingsEnabled,
            use_service_offering_seat_types: false,
          }
        : {
            name: enrollmentPeriod.name ?? "",
            active: enrollmentPeriod.active ?? false,
            key: enrollmentPeriod.key || "",
            use_service_offerings: enrollmentPeriod.use_service_offerings,
            use_service_offering_seat_types:
              enrollmentPeriod.use_service_offering_seat_types,
          },
    [isNewSeason, enrollmentPeriod, isServiceOfferingsEnabled]
  );

  const showOfferingsToggle =
    isServiceOfferingsEnabled ||
    (!isNewSeason && enrollmentPeriod.use_service_offerings);
  const showSeatTypesToggle =
    isMultiSeatTypeEnabled ||
    (!isNewSeason && enrollmentPeriod.use_service_offering_seat_types);

  const handleSubmit = (values: EnrollmentPeriodFormType) => {
    onSubmit({
      name: values.name?.trim(),
      active: values.active,
      key: values.key?.trim() || null,
      use_service_offerings: values.use_service_offerings,
      use_service_offering_seat_types:
        values.use_service_offerings && values.use_service_offering_seat_types,
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithZod(EnrollmentPeriodFormSchema)}
    >
      <VStack align="left" spacing={4} flexGrow="1" height="100%" as={Form}>
        {isNewSeason && <Heading>Create new enrollment period</Heading>}

        <FormControl display="flex" alignItems="center">
          <FormikSwitchInput<boolean>
            label="Enrollment period status"
            name="active"
            checked={[true, "Active"]}
            unchecked={[false, "Not Active"]}
            containerProps={{ direction: "row", gap: 2 }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Enrollment period name</FormLabel>
          <InputWithValidationControl id="name" name="name" isRequired={true} />
        </FormControl>

        <FormControl>
          <FormLabel>Enrollment period key</FormLabel>
          <InputWithValidationControl id="key" name="key" />
        </FormControl>

        <ServiceOfferingToggles
          isNewSeason={isNewSeason}
          showOfferingsToggle={showOfferingsToggle}
          showSeatTypesToggle={showSeatTypesToggle}
        />

        <Spacer />
        <AdminFormButtons insideTabPanel alignItems="center">
          <Button
            as={NavLink}
            to={organization
              .map((org) => Url.OrgAdmin.Enrollments.index(org))
              .withDefault("#")}
            isLoading={organization.isLoading()}
            variant="outline"
            colorScheme="gray"
          >
            Back
          </Button>
          <Spacer />
          <Button type="submit" marginLeft={4} isLoading={submitting}>
            {isNewSeason ? "Create" : "Update"}
          </Button>
        </AdminFormButtons>
      </VStack>
    </Formik>
  );
};

function ServiceOfferingToggles({
  isNewSeason,
  showOfferingsToggle,
  showSeatTypesToggle,
}: {
  isNewSeason: boolean;
  showOfferingsToggle: boolean;
  showSeatTypesToggle: boolean;
}) {
  if (!showOfferingsToggle) {
    return null;
  }

  return (
    <FormikSwitchableContent<boolean>
      label="Use offerings and categories instead of grades"
      name="use_service_offerings"
      checked={[true, ""]}
      unchecked={[false, ""]}
      isDisabled={!isNewSeason}
      showContent={(value) => value && showSeatTypesToggle}
      description={
        <>
          Enables the organization to use flexible offerings instead of
          prescribed grades. {/* TODO: Add help article slug */}
          <LearnMore article="" />
        </>
      }
    >
      <FormikSwitchableContent<boolean>
        label="Use staff assigned seats"
        name="use_service_offering_seat_types"
        checked={[true, ""]}
        unchecked={[false, ""]}
        isDisabled={!isNewSeason}
        showContent={() => false}
        description={
          <>
            Enables the organization to use programs as an additional dimension
            to schools, along with grades. Impacts forms, enrollment periods,
            and their capacity. {/* TODO: Add help article slug */}
            <LearnMore article="" />
          </>
        }
      >
        {null}
      </FormikSwitchableContent>
    </FormikSwitchableContent>
  );
}
